import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Calculator, SunMedium, ShieldCheck, MailCheck, Repeat } from "lucide-react";

import { pageSeo } from "@/lib/seo";
import { FINANCE_LOAN_CALCULATOR_PATH } from "@/content/finance";
import { SOLAR_LEASE_PLAN_PATH } from "@/content/financing-solar-lease-plan";

const REASSURANCE_POINTS = [
  { icon: ShieldCheck, label: "No Obligation" },
  { icon: MailCheck, label: "Figures Emailed To You" },
  { icon: Repeat, label: "Compare Before You Decide" },
];

const PATH = "/finance-calculator";
const TITLE = "Work Out Your Numbers | CRS Energy";
const DESCRIPTION =
  "Choose a calculator: model monthly loan payments with Ideal4Finance, or request indicative Solar Plan figures alongside your written quote.";

export const Route = createFileRoute("/finance-calculator")({
  head: () => ({
    ...pageSeo({
      path: PATH,
      title: TITLE,
      description: DESCRIPTION,
      ogType: "website",
    }),
    meta: [
      ...(pageSeo({ path: PATH, title: TITLE, description: DESCRIPTION, ogType: "website" }).meta ?? []),
    ],
  }),
  component: CalculatorsHubPage,
});

function CalculatorsHubPage() {
  return (
    <main className="bg-background min-h-screen">
      <section className="container-page pb-16 pt-12 md:pb-24 md:pt-20">
        <div className="max-w-3xl mb-10 md:mb-14">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground text-balance">
            Work Out Your Numbers
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Choose the option you'd like to explore.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 md:gap-8 max-w-5xl">
          {/* Card 1 — Loan Repayment Calculator */}
          <div className="flex flex-col rounded-2xl border border-border bg-card p-6 md:p-8 shadow-soft">
            <div className="flex items-center gap-3 mb-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
                <Calculator className="h-5 w-5" aria-hidden />
              </span>
              <h2 className="text-xl md:text-2xl font-bold text-foreground">
                Loan Repayment Calculator
              </h2>
            </div>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed flex-1">
              Model your monthly payments on a loan with Ideal4Finance - choose an APR,
              enter a price and term, and get the figures emailed to you.
            </p>
            <div className="mt-6">
              <a
                href={FINANCE_LOAN_CALCULATOR_PATH}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors w-full sm:w-auto"
              >
                Open The Loan Calculator
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
            </div>
          </div>

          {/* Card 2 — Solar Plan Figures */}
          <div className="flex flex-col rounded-2xl border border-border bg-card p-6 md:p-8 shadow-soft">
            <div className="flex items-center gap-3 mb-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
                <SunMedium className="h-5 w-5" aria-hidden />
              </span>
              <h2 className="text-xl md:text-2xl font-bold text-foreground">
                Solar Plan Figures
              </h2>
            </div>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed flex-1">
              The Solar Plan's monthly cost depends on your system size and chosen term.
              Tell us what you're interested in and we'll include indicative Solar
              Plan figures alongside your written quote.
            </p>
            <div className="mt-6">
              <a
                href={`${SOLAR_LEASE_PLAN_PATH}#solar-lease-quote`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors w-full sm:w-auto"
              >
                Request My Solar Plan Figures
                <ArrowRight className="h-4 w-4" aria-hidden />
              </a>
            </div>
          </div>
        </div>

        {/* Reassurance strip */}
        <div className="mt-10 md:mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 max-w-5xl">
          {REASSURANCE_POINTS.map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground"
            >
              <Icon className="h-4 w-4 text-accent" aria-hidden />
              {label}
            </span>
          ))}
        </div>
      </section>
    </main>
  );
}
